import React, { useEffect, useState } from 'react';
import { Loader2, UserCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const TIMES = ['08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'];
export const toUS = (iso) => (iso ? iso.split('-').slice(1).concat(iso.split('-')[0]).join('/') : '');

export const AssignRecertModal = ({ item, onClose, onAssigned }) => {
  const a = item.assignment;
  const [staff, setStaff] = useState(null);
  const [staffId, setStaffId] = useState(a?.staffId || '');
  const [date, setDate] = useState(a?.visitDate || item.suggestedVisitDate);
  const [time, setTime] = useState(a?.visitTime || '09:00 AM');
  const [notify, setNotify] = useState(!a);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/recert/assignable-staff').then(r => setStaff(r.data.staff || [])).catch(() => setStaff([]));
  }, []);

  const today = new Date().toISOString().split('T')[0];
  const endIso = (() => { const [m, d, y] = item.certPeriodEnd.split('/'); return `${y}-${m}-${d}`; })();
  const outsideWindow = date && (date < item.recertWindowStart || date > endIso);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post('/recert/assign', {
        patientId: item.patientId, certPeriodEnd: item.certPeriodEnd, staffId, visitDate: date, visitTime: time, notifyNurse: notify,
      });
      toast.success(r.data.message);
      onAssigned();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not assign. Please try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="assign-recert-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="bg-gray-900 p-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#d4af37] flex items-center gap-2"><UserCheck className="w-5 h-5" /> {a ? 'Update Recert Assignment' : 'Assign Recertification'}</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="assign-recert-close"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600"><strong>{item.patientName}</strong>, cert period ends {item.certPeriodEnd}. Recert window: {toUS(item.recertWindowStart)} to {item.certPeriodEnd}.</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nurse</label>
            {staff === null ? <Loader2 className="w-4 h-4 animate-spin" /> : staff.length === 0 ? (
              <p className="text-sm text-red-600" data-testid="assign-recert-no-staff">No staff found. Add nurses under Staff Management first.</p>
            ) : (
              <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="assign-recert-staff">
                <option value="">Select a nurse</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.role}){s.email ? '' : ', no email'}</option>)}
              </select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Visit date</label>
              <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="assign-recert-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <select value={time} onChange={(e) => setTime(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="assign-recert-time">
                {TIMES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {outsideWindow && <p className="text-xs text-amber-700" data-testid="assign-recert-window-warning">This date is outside the 5-day recert window.</p>}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="assign-recert-notify" />
            Email the visit details to the nurse
          </label>
          <button onClick={save} disabled={saving || !staffId || !date} data-testid="assign-recert-save"
            className="w-full py-2.5 rounded-full bg-[#d4af37] text-gray-900 font-semibold disabled:opacity-40 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {a ? 'Update Assignment' : 'Assign and Schedule Visit'}
          </button>
        </div>
      </div>
    </div>
  );
};
